'use client'

import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { ArrowRight, Zap, MessageSquare, BarChart3, Users, Clock, Shield } from 'lucide-react'

interface Feature {
  title: string
  description: string
  href?: string
}

interface AutomotiveFeaturesSectionProps {
  features: Feature[]
}

const iconMap = [Zap, MessageSquare, BarChart3, Users, Clock, Shield]

export function AutomotiveFeaturesSection({ features }: AutomotiveFeaturesSectionProps) {
  return (
    <section className="relative py-24 overflow-hidden bg-background">
      {/* Header */}
      <div className="container mx-auto px-6 mb-16">
        <div className="max-w-3xl">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="reveal text-3xl md:text-4xl lg:text-5xl font-bold mb-6"
          >
            Automotive-specific automation in one platform.
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="reveal reveal-delay-1 text-lg text-muted-foreground"
          >
            Your team gets clarity, your customers get instant responses, and leadership finally sees the full pipeline.
          </motion.p>
        </div>
      </div>

      {/* Features Grid */}
      <div className="container mx-auto px-6">
        <div className="grid md:grid-cols-2 gap-6 max-w-6xl mx-auto">
          {features.map((feature, index) => {
            const Icon = iconMap[index % iconMap.length]
            return (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30, filter: "blur(10px)" }}
                whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                viewport={{ once: true }}
                transition={{
                  delay: index * 0.1,
                  duration: 0.8,
                  ease: [0.16, 1, 0.3, 1]
                }}
                className={`reveal ${['reveal-delay-0', 'reveal-delay-1', 'reveal-delay-2', 'reveal-delay-3'][index % 4]}`}
              >
                <Link
                  to={feature.href ?? "#"}
                  className="group lift-hover relative block h-full rounded-3xl overflow-hidden bg-white dark:bg-zinc-900 p-8 transition-all duration-300"
                  style={{
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                  }}
                >
                  {/* Gradient Glow on Hover */}
                  <motion.div
                    className="absolute bottom-0 left-0 right-0 h-2/3 opacity-0 group-hover:opacity-60 transition-opacity duration-300"
                    style={{
                      background: 'radial-gradient(ellipse at bottom, rgba(47, 95, 232, 0.15) -20%, transparent 70%)',
                      filter: 'blur(40px)',
                    }}
                  />

                  {/* Bottom Border Glow */}
                  <motion.div
                    className="absolute bottom-0 left-0 right-0 h-[2px] opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    style={{
                      background: 'linear-gradient(90deg, rgba(47, 95, 232, 0.1) 0%, rgba(47, 95, 232, 0.5) 50%, rgba(47, 95, 232, 0.1) 100%)',
                      boxShadow: '0 0 15px 3px rgba(47, 95, 232, 0.2)',
                    }}
                  />

                  {/* Content */}
                  <div className="relative z-10 flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gradient-to-br from-[#E8EFF8] to-[#D4E1F4] dark:from-[#2F5FE8]/20 dark:to-[#7EA0F5]/20 flex items-center justify-center border border-[#D4E1F4] dark:border-[#7EA0F5]/20 group-hover:scale-110 transition-transform duration-300">
                      <Icon className="h-6 w-6 text-[#2F5FE8] dark:text-[#7EA0F5]" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-3">
                        <h3 className="text-lg font-bold text-foreground">
                          {feature.title}
                        </h3>
                        <ArrowRight className="h-4 w-4 text-[#2F5FE8] translate-x-0 opacity-0 group-hover:translate-x-1 group-hover:opacity-100 transition-all duration-300" />
                      </div>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                </Link>
              </motion.div>
            )
          })}
        </div>
      </div>
    </section>
  )
}
